import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  LinearProgress,
  Collapse,
  Tooltip,
  Skeleton
} from '@mui/material'
import {
  CheckCircleOutline,
  PersonSearch,
  AlternateEmail,
  MailOutline,
  Send,
  InfoOutlined
} from '@mui/icons-material'
import DartIcon from '../atoms/DartIcon'

const TASK_CONFIG = {
  relevancy: { icon: DartIcon, color: '#7C4DFF', labelKey: 'mailTemplate.campaigns.dashboard.task.relevancyCheck' },
  contacts: { icon: PersonSearch, color: '#0091EA', labelKey: 'mailTemplate.campaigns.dashboard.task.contactSearch' },
  emailQuery: { icon: AlternateEmail, color: '#E65100', labelKey: 'mailTemplate.campaigns.dashboard.task.emailAddressQuery' },
  content: { icon: MailOutline, color: '#00BFA5', labelKey: 'mailTemplate.campaigns.dashboard.task.contentGenerate' },
  mailing: { icon: Send, color: '#1565C0', labelKey: 'mailTemplate.campaigns.dashboard.task.mailing' },
  unknown: { icon: MailOutline, color: '#78909C', labelKey: 'mailTemplate.campaigns.dashboard.task.loading' }
}

const TYPE_ORDER = ['relevancy', 'contacts', 'emailQuery', 'content', 'mailing', 'unknown']

const DISMISS_DELAY = 5000
const LABEL_WIDTH = 160

const DONE_COLOR = '#2E7D32'

const getPhaseProgress = (phase, isDone) => {
  const isComplete = isDone || (phase.total > 0 && phase.completed >= phase.total)
  const isIndeterminate = !isDone && phase.total === 0 && phase.completed === 0

  const value = isComplete
    ? 100
    : (phase.total > 0 ? Math.round((phase.completed / phase.total) * 100) : 0)

  return { isComplete, value, isIndeterminate }
}

const progressBarSx = (barColor) => ({
  height: 4,
  borderRadius: 2,
  backgroundColor: `${barColor}20`,
  '& .MuiLinearProgress-bar': {
    borderRadius: 2,
    backgroundColor: barColor,
    transition: 'transform 0.4s ease'
  }
})

const PhaseBar = ({ phase, isDone, color }) => {
  const { isComplete, value, isIndeterminate } = getPhaseProgress(phase, isDone)
  const barColor = isComplete ? DONE_COLOR : color

  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: isDone ? DONE_COLOR : 'text.primary', mb: 0.25, display: 'block' }}>
        {phase.label}
      </Typography>
      <LinearProgress
        variant={isIndeterminate ? 'indeterminate' : 'determinate'}
        value={value}
        sx={progressBarSx(barColor)}
      />
    </Box>
  )
}

const ProgressBar = ({ task, color }) => {
  const isDone = task.status === 'FINISHED'
  const barColor = isDone ? DONE_COLOR : color

  const percentage = task.total > 0
    ? Math.round((task.completed / task.total) * 100)
    : 0

  if (task.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="rounded" height={4} sx={{ borderRadius: 2 }} />
        </Box>
        <Skeleton variant="text" width={30} sx={{ fontSize: '0.7rem' }} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
      <Box sx={{ flex: 1 }}>
        {task.phases
          ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <PhaseBar phase={task.phases.phase1} isDone={isDone} color={color} />
              <PhaseBar phase={task.phases.phase2} isDone={isDone} color={color} />
            </Box>
            )
          : (
            <LinearProgress
              variant="determinate"
              value={isDone ? 100 : percentage}
              sx={progressBarSx(barColor)}
            />
            )
        }
      </Box>

      <Typography
        variant="caption"
        component="span"
        sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem', minWidth: 50, textAlign: 'right' }}
      >
        {isDone
          ? <CheckCircleOutline sx={{ fontSize: 14, color: DONE_COLOR }} />
          : (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {task.phases
                ? `${task.phases.phase2.completed}/${task.phases.phase2.total}`
                : `${task.completed}/${task.total}`
              }
            </Box>
            )
        }
      </Typography>
    </Box>
  )
}

const TaskGroup = ({ type, tasks, isFirst }) => {
  const { t } = useTranslation()
  const config = TASK_CONFIG[type] || TASK_CONFIG.relevancy
  const Icon = config.icon

  const allDone = tasks.every(t => t.status === 'FINISHED')
  const isMailingInProgress = type === 'mailing' && !allDone

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pl: 12,
        pr: 5,
        py: 1,
        ...(!isFirst && { borderTop: '1px solid', borderColor: 'divider' }),
        backgroundColor: allDone ? `${DONE_COLOR}0A` : 'rgba(0, 0, 0, 0.01)',
        transition: 'background-color 0.3s'
      }}
    >
      <Box
        sx={{
          width: LABEL_WIDTH,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Icon sx={{ fontSize: 16, color: allDone ? DONE_COLOR : config.color, flexShrink: 0 }} />
        <Tooltip title={t(config.labelKey)} arrow>
          <Typography
            variant="caption"
            noWrap
            sx={{
              fontWeight: 500,
              color: allDone ? DONE_COLOR : 'text.primary',
              fontSize: '0.75rem',
              letterSpacing: '-0.01em'
            }}
          >
            {t(config.labelKey)}
          </Typography>
        </Tooltip>
        {isMailingInProgress && (
          <Tooltip title={t('mailTemplate.campaigns.dashboard.task.mailingHint')} arrow placement="right">
            <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {tasks.map(task => (
          <ProgressBar
            key={task.id}
            task={task}
            color={config.color}
          />
        ))}
      </Box>
    </Box>
  )
}

const CampaignProgressBars = ({ tasks = [] }) => {
  const [dismissed, setDismissed] = useState({})

  const timersRef = useRef({})

  const groups = useMemo(() => {
    const visible = tasks.filter(
      t => (t.status === 'IN_PROGRESS' || t.status === 'STARTED' || t.status === 'SUBMITTED' || (t.status === 'FINISHED' && !dismissed[t.id]))
    )

    const grouped = {}
    visible.forEach(task => {
      if (!grouped[task.type]) grouped[task.type] = []
      grouped[task.type].push(task)
    })

    return TYPE_ORDER
      .filter(type => grouped[type]?.length > 0)
      .map(type => ({ type, tasks: grouped[type] }))
  }, [tasks, dismissed])

  useEffect(() => {
    tasks.forEach(task => {
      if (task.status === 'FINISHED' && !dismissed[task.id] && !timersRef.current[task.id]) {
        timersRef.current[task.id] = setTimeout(() => {
          setDismissed(prev => ({ ...prev, [task.id]: true }))
          delete timersRef.current[task.id]
        }, DISMISS_DELAY)
      }
    })
  }, [tasks, dismissed])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      Object.values(timers).forEach(clearTimeout)
    }
  }, [])

  useEffect(() => {
    const taskIds = new Set(tasks.map(t => t.id))

    setDismissed(prev => {
      const next = {}
      Object.keys(prev).forEach(id => {
        if (taskIds.has(id)) next[id] = prev[id]
      })
      return next
    })
  }, [tasks])

  if (groups.length === 0) return null

  return (
    <Box sx={{ py: 0.5, borderBottom: '2px solid', borderColor: 'divider' }}>
      {groups.map((group, idx) => (
        <Collapse key={group.type} in timeout={300}>
          <TaskGroup
            type={group.type}
            tasks={group.tasks}
            isFirst={idx === 0}
          />
        </Collapse>
      ))}
    </Box>
  )
}

export default CampaignProgressBars
